import { inject, Injectable } from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';
import { environment } from '@env/environment';
import { NotificationType, SectionType } from '@core/enums';
import { Observable } from 'rxjs';
import { HttpClient } from '@angular/common/http';

@Injectable({
  providedIn: 'root',
})
export class ImageConversionService {
  private readonly _sanitizer = inject(DomSanitizer);
  private readonly _http = inject(HttpClient);

  private readonly _apiUrl = environment.apiUrl;

  generateImageUrlWithToken(id: any, type: SectionType) {
    const expirationTime = Date.now() + 60 * 1000; // Current time + 2 minutes (in milliseconds)
    return this._apiUrl + 'proxy/image/' + btoa(JSON.stringify({ imageId: id, expirationTime: expirationTime, type: type }));
  }

  deleteEntityImage(sectionType: NotificationType, entityId: number): Observable<any> {
    return this._http.get(`${this._apiUrl}/delete-image/${sectionType}/${entityId}`);
  }

  convertImage(image: any) {
    return new Promise((resolve, reject) => {
      this.imageUrlToBlob(image)
        .then((imageBlob) => {
          const blob = new Blob([imageBlob], { type: 'image/png' });
          const urlCreator: any = window.URL || window.webkitURL;
          const imgURL = this._sanitizer.bypassSecurityTrustUrl(urlCreator.createObjectURL(blob));
          resolve(imgURL);
        })
        .catch((error) => {
          console.error('Error converting image URL to blob:', error);
        });
    });
  }

  async imageUrlToBlob(imageUrl: RequestInfo | URL) {
    const response = await fetch(imageUrl);
    return await response.blob();
  }

  convertBase64ToSafeUrl(base64Url: string) {
    const base64Data = base64Url.substring(20);
    return this._sanitizer.bypassSecurityTrustResourceUrl(this.convertBase64ToBlobUrl(base64Data));
  }

  convertBase64ToBlobUrl(base64Url: string): string | null {
    // Check if the input is a valid string
    if (!base64Url || typeof base64Url !== 'string') {
      return null;
    }

    // Remove the prefix (e.g., data:image/jpeg;base64,) from the Base64 URL
    const base64Data = base64Url.replace(/^data:image\/(png|jpeg|jpg);base64,/, '');

    // Convert the Base64 data to a Blob
    const byteCharacters = atob(base64Data);
    const byteArrays = [];
    for (let offset = 0; offset < byteCharacters.length; offset += 512) {
      const slice = byteCharacters.slice(offset, offset + 512);
      const byteNumbers = new Array(slice.length);
      for (let i = 0; i < slice.length; i++) {
        byteNumbers[i] = slice.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      byteArrays.push(byteArray);
    }
    const blob = new Blob(byteArrays, { type: 'image/png' });

    return URL.createObjectURL(blob);
  }

  async convertBase64ToFile(base64String: string, fileName: string): Promise<File> {
    const res = await fetch(base64String);
    const blob = await res.blob();
    return new File([blob], fileName, { type: 'image/png' });
  }
}
